import { AlignmentMap, ORIENTATION_ENUM, TCoreLayoutOptions } from '../types/options';
import { getValueFromPalette, TPaletteState } from '../utils/styles';

const px = (value: unknown): string | undefined => (typeof value === 'number' ? `${value}px` : undefined);

const alignmentMap: Record<keyof AlignmentMap, string> = {
    top: 'flex-start',
    start: 'flex-start',
    center: 'center',
    bottom: 'flex-end',
    end: 'flex-end',
    space_between: 'space-between',
    space_around: 'space-around',
    space_evenly: 'space-evenly',
    stretch: 'stretch',
};

const calcWidth = (val?: number | string): string | undefined => {
    if (!val) return undefined;
    if (typeof val === 'number') return `${val}px`;
    if (val.endsWith('%') || val.endsWith('content')) return val;
    return `${val}px`;
};

const calcHeight = (val?: number | string): string | undefined => {
    if (!val) return undefined;
    if (typeof val === 'number') return `${val}px`;
    if (val.endsWith('%')) return val;
    return `${val}px`;
};

export const buildLayoutCssVars = (palette: TCoreLayoutOptions, state: TPaletteState = {}): Record<string, string> => {
    const margin = palette.margin ?? {};
    const padding = palette.padding ?? {};
    const orientation = palette.orientation ?? ORIENTATION_ENUM.VERTICAL;

    let flexDirection: string | undefined;
    let justifyContent: string | undefined;
    let alignItems: string | undefined;

    if (orientation !== ORIENTATION_ENUM.BOX) {
        flexDirection = orientation === ORIENTATION_ENUM.HORIZONTAL ? 'row' : 'column';
        if (orientation === ORIENTATION_ENUM.HORIZONTAL) {
            justifyContent = alignmentMap[palette.lineX ?? 'start'];
            alignItems = alignmentMap[palette.lineY ?? 'center'];
        } else {
            justifyContent = alignmentMap[palette.lineY ?? 'start'];
            alignItems = alignmentMap[palette.lineX ?? 'center'];
        }
    }

    const vars: Record<string, string | undefined> = {
        '--cw-layout-flex-direction': flexDirection,
        '--cw-layout-justify-content': justifyContent,
        '--cw-layout-align-items': alignItems,
        '--cw-layout-width': calcWidth(palette.width),
        '--cw-layout-min-width': calcWidth(palette.minWidth),
        '--cw-layout-height': calcHeight(palette.height),
        '--cw-layout-min-height': calcHeight(palette.minHeight),
        '--cw-layout-bg': getValueFromPalette(palette, 'backgroundColor', state),
        '--cw-layout-bg-hover': palette.hovered?.backgroundColor,
        '--cw-layout-bg-pressed': palette.pressed?.backgroundColor,
        '--cw-layout-border-radius': px(palette.borderRadius),
        '--cw-layout-border-width': px(palette.borderStroke),
        '--cw-layout-border-color': getValueFromPalette(palette, 'borderColor', state),
        '--cw-layout-box-shadow': palette.boxShadow,
        '--cw-layout-overflow': palette.overflow,
        '--cw-layout-margin-top': px(margin.top),
        '--cw-layout-margin-right': px(margin.right),
        '--cw-layout-margin-bottom': px(margin.bottom),
        '--cw-layout-margin-left': px(margin.left),
        '--cw-layout-padding-top': px(padding.top),
        '--cw-layout-padding-right': px(padding.right),
        '--cw-layout-padding-bottom': px(padding.bottom),
        '--cw-layout-padding-left': px(padding.left),
        '--cw-layout-gap': px(palette.gap),
        '--cw-layout-flex-shrink': typeof palette.flexShrink === 'number' ? String(palette.flexShrink) : undefined,
        '--cw-layout-opacity': typeof palette.opacity === 'number' ? String(palette.opacity) : undefined,
    };

    return Object.fromEntries(Object.entries(vars).filter((entry): entry is [string, string] => entry[1] !== undefined));
};
